import React, { useEffect, useRef, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Alert, Button, ButtonGroup, Card, CardBody, Container, Input, Spinner, Table } from "reactstrap";

import { apiFetch, API } from "../../services/api";
import { readError } from "../../helpers/whatsappTemplates";
import { KINDS, listQuery, loadListState, periodOptions, saveListState, statusLabel } from "../../helpers/partnerReports";

const STATUS_COLORS = { draft: ["#eef2ff", "#3730a3"], sending: ["#fff7ed", "#9a3412"], sent: ["#ecfdf5", "#065f46"], failed: ["#fef2f2", "#991b1b"] };

// Reportings partenaires : brouillons du mois / du trimestre / de l'année, générés par le robot, à relire puis envoyer.
// L'écran (onglet, rythme, période) est gardé dans l'URL et en mémoire locale : on le retrouve en revenant.
export function ReportsList() {
    const location = useLocation();
    const history = useHistory();
    const [initial] = useState(() => loadListState(location.search));
    const [kind, setKind] = useState(initial.kind);
    const [type, setType] = useState(initial.type); // distributeurs | marques
    const [period, setPeriod] = useState(initial.period);
    const [data, setData] = useState(null);
    const [configs, setConfigs] = useState([]);
    const [error, setError] = useState(null);
    const [pending, setPending] = useState({});
    const timer = useRef(null);

    const load = (k = kind, p = period) => apiFetch(`${API.back}/partner-reports?kind=${k}&period=${p}`, { market: false })
        .then(async (res) => { if (!res.ok) throw new Error(await readError(res)); return res.json(); })
        .then((d) => { setData(d); return d; })
        .catch((e) => { setError("Rapports indisponibles : " + e.message); setData({ reports: [] }); });

    useEffect(() => {
        apiFetch(API.back + "/partner-report-configs", { market: false }).then((r) => (r.ok ? r.json() : [])).then(setConfigs).catch(() => setConfigs([]));
        return () => clearInterval(timer.current);
    }, []);
    useEffect(() => { setData(null); load(kind, period); }, [kind, period]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        saveListState({ type, kind, period });
        const q = listQuery({ type, kind, period });
        if (location.search !== q) history.replace(location.pathname + q);
    }, [type, kind, period]); // eslint-disable-line react-hooks/exhaustive-deps

    const switchKind = (k) => { setKind(k); setPeriod(periodOptions(k)[0]); };

    // Génération à la demande : le robot tourne en asynchrone, on recharge 12 × 5 s.
    const generate = async (partner) => {
        setError(null);
        const res = await apiFetch(API.back + "/partner-report-regenerate", { method: "POST", market: false, body: JSON.stringify({ partner, kind, period }) });
        if (!res.ok) { setError(await readError(res)); return; }
        setPending((p) => ({ ...p, [partner]: true }));
        let n = 0;
        clearInterval(timer.current);
        timer.current = setInterval(async () => {
            n++;
            const d = await load();
            const done = d && (d.reports || []).some((r) => r.partner === partner);
            if (done || n >= 12) {
                clearInterval(timer.current);
                setPending((p) => ({ ...p, [partner]: false }));
            }
        }, 5000);
    };

    const typeOf = (x) => x.partnerType || "merchant";
    const reports = (data?.reports || []).filter((r) => typeOf(r) === type);
    const typedConfigs = configs.filter((c) => typeOf(c) === type);
    const wanted = typedConfigs.filter((c) => c.enabled && c[kind]);
    const missing = typedConfigs.filter((c) => !reports.some((r) => r.partner === `${c.merchantKey}#${c.market}`));

    return (
        <Container fluid className="p-0">
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                <h1 className="h3 mb-0">Reportings partenaires</h1>
                <div className="d-flex align-items-center">
                    <ButtonGroup className="mr-2">
                        <Button color={type === "merchant" ? "primary" : "light"} onClick={() => setType("merchant")}>Distributeurs</Button>
                        <Button color={type === "brand" ? "primary" : "light"} onClick={() => setType("brand")}>Marques</Button>
                    </ButtonGroup>
                    <ButtonGroup className="mr-2">
                        {KINDS.map(([id, label]) => <Button key={id} color={kind === id ? "primary" : "light"} onClick={() => switchKind(id)}>{label}</Button>)}
                    </ButtonGroup>
                    <Input type="select" value={period} onChange={(e) => setPeriod(e.target.value)} style={{ width: 140 }}>
                        {periodOptions(kind).map((p) => <option key={p} value={p}>{p}</option>)}
                    </Input>
                </div>
            </div>
            {error && <Alert color="danger">{error}</Alert>}
            <Card>
                <CardBody className="p-0">
                    {!data ? <div className="p-3"><Spinner size="sm" /></div> : (
                        <Table className="mb-0" hover responsive>
                            <thead><tr><th>Partenaire</th><th>Marché</th><th>Statut</th><th>Objet</th><th>Envoi</th><th /></tr></thead>
                            <tbody>
                                {reports.map((r) => {
                                    const [bg, color] = STATUS_COLORS[r.status] || STATUS_COLORS.draft;
                                    const url = `/partner-reports/report/${encodeURIComponent(r.partner)}/${encodeURIComponent(`${period}#${kind}`)}`;
                                    return (
                                        <tr key={r.partner}>
                                            <td><Link to={url}><b>{r.displayName}</b></Link>
                                                {(r.warnings || []).length > 0 && <div className="small" style={{ color: "#b45309" }} title={r.warnings.join("\n")}>⚠ {r.warnings.length} avertissement{r.warnings.length > 1 ? "s" : ""}</div>}</td>
                                            <td>{(r.market || "").toUpperCase()}</td>
                                            <td><span style={{ padding: "3px 9px", borderRadius: 999, fontSize: 12, fontWeight: 700, background: bg, color }}>{statusLabel(r.status)}</span></td>
                                            <td className="small">{r.subject}</td>
                                            <td className="small">{r.status === "sent" ? new Date(r.sentAt).toLocaleString("fr-FR") : r.canSend ? "Prêt" : <span className="text-muted">{r.cannotSendReason}</span>}</td>
                                            <td className="text-right"><Link className="btn btn-sm btn-light" to={url}>Ouvrir</Link></td>
                                        </tr>
                                    );
                                })}
                                {missing.map((c) => {
                                    const partner = `${c.merchantKey}#${c.market}`;
                                    return (
                                        <tr key={partner} className="text-muted">
                                            <td><b>{c.displayName}</b>{!wanted.includes(c) && <div className="small">non prévu pour ce type</div>}</td>
                                            <td>{c.market.toUpperCase()}</td>
                                            <td colSpan={3} className="small">Pas encore de brouillon pour cette période</td>
                                            <td className="text-right"><Button size="sm" color="light" disabled={pending[partner]} onClick={() => generate(partner)}>{pending[partner] ? <Spinner size="sm" /> : "Générer maintenant"}</Button></td>
                                        </tr>
                                    );
                                })}
                                {reports.length === 0 && missing.length === 0 && <tr><td colSpan={6} className="p-3">{type === "brand" ? "Aucune marque configurée : commence par la page « Partenaires » (onglet Marques)." : "Aucun distributeur configuré : commence par la page « Partenaires »."}</td></tr>}
                            </tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>
        </Container>
    );
}
